import { memo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Spinner from "~/common/components/Spinner/Spinner";
import { useOnMount } from "~/common/hooks/useOnMount";
import { type RootState } from "~/app/store";
import { EmptyCartPage } from "~/features/cart/cart/components/EmptyCartPage";
import { B2BCartPage } from "~/features/cart/cart/components/B2BCartPage";
import { CartPageContextProvider } from "~/features/cart/cart/CartPageContext";
import { cartPageLoadThunk } from "~/features/cart/cart/redux/cartPageLoadThunk";
import { userIsCustomer } from "~/domains/user/utils/roles";
import { B2CCartPage } from "~/features/cart/B2CCart/components/B2CCartPage/B2CCartPage";
import { FCCGapProductRecommendation } from "~/features/saleRecommendations/components/SchaefflerFCCGapProductsRecommendationComponent";
import { PurchasePlaceProvider } from "~/app/analytics/ga/recommendations/RecommendationContexts";

export const CartPage = memo(() => {
  const dispatch = useDispatch();
  useOnMount(() => {
    dispatch(cartPageLoadThunk());
  });

  const isCartLoading = useSelector(selectIsCartLoading);
  const isCartEmpty = useSelector(selectIsCartEmpty);

  if (isCartLoading) {
    return <Spinner />;
  }

  if (isCartEmpty) {
    return (
      <>
        <EmptyCartPage />
        <FCCGapProductRecommendation />
      </>
    );
  }

  return !userIsCustomer() ? <B2CCartPage /> : <B2BCartPage />;
});

const selectIsCartLoading = (state: RootState) => state.cartPage.loading;
const selectIsCartEmpty = (state: RootState) => selectIsCartLoading(state) || state.cart.cartEntries.length === 0;

export interface Props {
  disableCheckout: boolean;
  showSubtotal: boolean;
}

export function createCartPage(props: Props) {
  return (
    <PurchasePlaceProvider value={"CART"}>
      <CartPageContextProvider initialDisableCheckout={props.disableCheckout} initialShowSubtotal={props.showSubtotal}>
        <CartPage />
      </CartPageContextProvider>
    </PurchasePlaceProvider>
  );
}
